<template>
    <div class="staffs-license-assign">
        <licences :licenses="licenses" :versionedLicenses="versionedLicenses" />
        <h3 class="mt-4">
            {{ $l10n.__('Attribution des licences') }}
        </h3>
        <p
            class="m-0 fs-3 lh-big"
            v-html="
                $t.__(
                    'sellsyaccountmanager.SubscriptionManager.Staffs.licence_count_on_account',
                    total,
                )
            "
        ></p>
        <p class="grey m-0 fs-3 lh-big">
            {{
                $l10n.__(
                    'Attribuez les en fonction des besoins de vos collaborateurs afin qu’ils puissent utiliser les différents modules proposés dans Sellsy.',
                )
            }}
        </p>

        <div class="flex-row br staff-form-container fs-3 p-2 mt-3 gray-0-bkg">
            <span class="semibold mr-3">{{
                $l10n.__('%s collaborateurs au total', totalUser)
            }}</span>
            <CompassButton
                :label="$t.__('sellsyaccountmanager.SubscriptionManager.Staffs.add_staff')"
                type="primary"
                @click="openPopup"
            />
        </div>

        <el-table :data="staffs" style="width: 100%">
            <el-table-column :label="$t.__('common.labels.name')">
                <template slot-scope="scope">
                    <avatar-randomized
                        :firstname="scope.row.firstname"
                        :lastname="scope.row.lastname"
                        :avatarImage="scope.row.avatar"
                    />
                    <span class="ml-2 semibold">{{
                        scope.row.firstname + ' ' + scope.row.lastname
                    }}</span>
                </template>
            </el-table-column>
            <el-table-column
                :label="$t.__('sellsyaccountmanager.SubscriptionManager.Staffs.job_title')"
            >
                <template slot-scope="scope">
                    <span class="darkgrey">{{ scope.row.position }}</span>
                </template>
            </el-table-column>
            <el-table-column :width="224" :label="$t.__('common.labels.licence_type')">
                <template slot-scope="scope">
                    <select-loader
                        v-if="scope.row.loading"
                        :message="$t.__('common.labels.allocation_in_progress')"
                    />
                    <el-select
                        v-else
                        class="w-2"
                        :value="scope.row.license"
                        @change="updateLicense($event, scope.row)"
                    >
                        <el-option
                            v-for="license in availableLicenses"
                            :key="license.value"
                            :disabled="license.disabled"
                            :label="license.label"
                            :value="license.value"
                        >
                        </el-option>
                    </el-select>
                </template>
            </el-table-column>

            <template slot="empty">
                <div class="staffs-license-assign__no-result">
                    <CompassEmptyState
                        illustrationPath="/ressources/logged/img/illustrations/no-results.svg"
                        :caption="labels.empty"
                    />
                </div>
            </template>

            <infinite-loading slot="append" @infinite="infiniteHandler">
                <div slot="spinner" class="py-2">{{ $l10n.__('Chargement en cours...') }}</div>
                <div slot="no-more"></div>
                <div slot="no-results"></div>
            </infinite-loading>
        </el-table>
    </div>
</template>

<script>
import Licences from 'vcomponents/sellsyaccountmanager/Licences.vue';
import SelectLoader from './SelectLoader.vue';
import InfiniteLoading from 'vue-infinite-loading';
import { createNamespacedHelpers } from 'vuex';
import AvatarRandomized from 'vcomponents/utilities/AvatarRandomized.vue';

const { mapState, mapActions } = createNamespacedHelpers('subscriptionManager');
const { mapState: mapStateUser } = createNamespacedHelpers('user');
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;
export default {
    components: {
        Licences,
        AvatarRandomized,
        InfiniteLoading,
        SelectLoader,
    },
    props: {
        licenseAssignement: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: [],
            totalUser: 0,
            labels: {
                empty: this.$l10n.__('Aucun collaborateur créé pour le moment'),
            },
        };
    },
    computed: {
        ...mapState({
            availableLicenses(state) {
                return state.availableLicenses;
            },
            versionedLicenses(state) {
                return state.versionedLicenses;
            },
            licenses(state) {
                return state.licenses;
            },
            /**
             * Return array of the list of staff users
             */
            staffs(state) {
                const staffList = state.staffs.map((staff, index) => {
                    staff.loading = this.loading[index];
                    staff.license = staff.license === null ? 'null' : staff.license;
                    return staff;
                });
                this.totalUser = staffList.length;
                return staffList;
            },
            total: function (state) {
                if (state.licenses.length === 0) {
                    return 0;
                }
                return state.licenses.reduce(
                    (accumulator, license) => accumulator + license.total,
                    0,
                );
            },
        }),
        ...mapStateUser({
            staffId: 'id',
        }),
    },
    created() {
        if (!this.licenseAssignement && this.staffs.length === 0) {
            this.addStaffs().then(({ data }) => {
                this.loading.push(...Array.from(data.data, () => false));
            });
        }
    },
    methods: {
        ...popupPanelMapActions(['open']),
        ...mapActions(['addStaffs', 'setLicenseForStaff']),

        /**
         * Open a popup throught call of open() from popupPanel.js
         */
        openPopup() {
            this.open({
                key: 'staffForm',
                props: {
                    message: window.i18n.__(
                        'Vous souhaitez ajouter un collaborateur à votre compte Sellsy ?',
                    ),
                    title: window.i18n.__('Ajouter un collaborateur'),
                },
            });
        },
        infiniteHandler($state) {
            this.addStaffs()
                .then(({ data }) => {
                    if (data.pagination.offset > data.pagination.total) {
                        $state.complete();
                    } else {
                        $state.loaded();
                    }
                    this.loading.push(...Array.from(data.data, () => false));
                })
                .catch(() => {
                    $state.complete();
                });
        },
        updateLicense(value, staff) {
            const index = this.staffs.map((staff) => staff.id).indexOf(staff.id);
            this.loading.splice(index, 1, true);
            this.setLicenseForStaff({
                staffId: staff.id,
                licenseId: value === 'null' ? null : value,
            })
                .then(() => {
                    this.loading.splice(index, 1, false);
                })
                .catch(() => {
                    Push.error(
                        this.$l10n.__('Une erreur est survenue, merci de réessayer plus tard'),
                    );
                    this.loading.splice(index, 1, false);
                });
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~@sellsy/styleguide/src/_variables';

.el-table:before {
    content: none;
}
.staff-form-container {
    justify-content: right;
    align-items: center;
}

.staffs-license-assign {
    &__no-result {
        margin-top: var(--spacing-5);
    }
}
</style>
